// NewsHeader.jsx - Version corrigée pour le mode découverte
import React, { useState, useMemo } from 'react';
import {
  Search,
  Bell,
  Settings,
  Target,
  X,
  RefreshCw,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Info,
  Brain,
  Clock,
  BarChart3,
  Zap,
  Save,
  BookOpen,
  Loader2
} from 'lucide-react';

import EnhancedMarkdown from './EnhancedMarkdown';

const NewsHeader = ({
  selectedObjectiveForNews,
  availableObjectives = [],
  loadingObjectives = false,
  objectivesError = null,
  isDiscovering = false,
  searchQuery = '',
  newsSynthesis = '',
  loadingSynthesis = false,
  synthesisArticles = [],
  filteredNews = [],
  semanticAnalysisInfo = null,
  showSemanticAnalysis = true,
  selectedTheme = 'all',
  themes = [],
  onSearchQueryChange,
  onObjectiveSelect,
  onDiscoverForObjective,
  onThemeSelect,
  onShowWatchlist,
  onGenerateSynthesis,
  onReadNews,
  onResetSynthesis
}) => {
  // ================================
  // ETATS LOCAUX
  // ================================

  const [showObjectiveDropdown, setShowObjectiveDropdown] = useState(false);
  const [expandedSynthesis, setExpandedSynthesis] = useState(false);
  const [objectiveSearchQuery, setObjectiveSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);

  // ================================
  // FONCTIONS UTILITAIRES
  // ================================
  
  const getObjectiveDisplayName = (objective) => {
    if (!objective) return 'Objectif sans nom';
    return objective.name || objective.shortTitle || objective.title || 'Objectif sans nom';
  };

  const sortObjectivesByDate = (objectives) => {
    return [...objectives].sort((a, b) => {
      const dateA = a.createdAt ? (a.createdAt.seconds ? new Date(a.createdAt.seconds * 1000) : new Date(a.createdAt)) : new Date(0);
      const dateB = b.createdAt ? (b.createdAt.seconds ? new Date(b.createdAt.seconds * 1000) : new Date(b.createdAt)) : new Date(0);
      return dateB - dateA;
    });
  };

  const formatObjectiveDate = (objective) => {
    if (!objective.createdAt) return '';
    const date = objective.createdAt.seconds ? new Date(objective.createdAt.seconds * 1000) : new Date(objective.createdAt);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit',
      year: '2-digit'
    });
  };

  // ================================
  // CORRECTION 4: Fonction pour actualiser la synthèse
  // ================================

  const handleRefreshSynthesis = async () => {
    console.log('🔄 Actualisation de la synthèse demandée');
    
    if (!onGenerateSynthesis) {
      console.warn('⚠️ Fonction onGenerateSynthesis non disponible');
      return;
    }

    try {
      // Vérifier qu'il y a des articles à synthétiser
      if (filteredNews.length === 0) {
        console.log('ℹ️ Aucun article à synthétiser');
        return;
      }

      console.log(`📊 Régénération synthèse basée sur ${filteredNews.length} articles`);
      await onGenerateSynthesis();
      
    } catch (error) {
      console.error('❌ Erreur actualisation synthèse:', error);
    }
  };

  // ================================
  // COMPOSANTS UTILITAIRES
  // ================================

  const DiscoveryProgressIndicator = () => {
    if (!isDiscovering) return null;

    return (
      <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-lg border border-blue-200">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Analyse en cours...</span>
      </div>
    );
  };

  const SemanticAnalysisInfo = () => {
    if (!showSemanticAnalysis || !selectedObjectiveForNews || !semanticAnalysisInfo || 
        semanticAnalysisInfo.totalAnalyzed === 0 || isDiscovering) {
      return null;
    }

    return (
      <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Brain className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-blue-900">Analyse des actualités</h4>
            <p className="text-sm text-blue-700">
              {semanticAnalysisInfo.relevantFound} sources pertinentes détectées sur {semanticAnalysisInfo.totalAnalyzed} analysées
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-purple-600">{semanticAnalysisInfo.avgScore.toFixed(1)}/10</div>
            <div className="text-sm text-gray-600">Score moyen</div>
          </div>
        </div>
        
        <div className="bg-white border border-blue-200 rounded-lg p-4">
          <div className="grid grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 mb-1">{semanticAnalysisInfo.totalAnalyzed}</div>
              <div className="text-sm text-blue-600">Articles analysés</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 mb-1">{semanticAnalysisInfo.relevantFound}</div>
              <div className="text-sm text-blue-600">Sources pertinentes</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 mb-1">
                {Math.round((semanticAnalysisInfo.relevantFound / semanticAnalysisInfo.totalAnalyzed) * 100)}%
              </div>
              <div className="text-sm text-blue-600">Taux de pertinence</div>
            </div>
          </div>
          
          <div className="mt-4 pt-3 border-t border-blue-200">
            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-600">Qualité :</span>
              <div className="flex items-center gap-2">
                {semanticAnalysisInfo.relevantFound >= 5 ? (
                  <>
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-700">Excellente</span>
                  </>
                ) : semanticAnalysisInfo.relevantFound >= 2 ? (
                  <>
                    <Info className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">Satisfaisante</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                    <span className="text-sm font-medium text-orange-700">Limitée</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ================================
  // FONCTIONS DE GESTION DES OBJECTIFS (conservées)
  // ================================

  const calculateSimilarity = (query, objective) => {
    const queryLower = query.toLowerCase();
    const titleLower = getObjectiveDisplayName(objective).toLowerCase();
    const themeLower = (objective.theme || objective.chapitre || '').toLowerCase();
    
    let titleScore = 0;
    if (titleLower.includes(queryLower)) {
      titleScore = 0.8;
    } else {
      const queryWords = queryLower.split(' ').filter(w => w.length > 2);
      const titleWords = titleLower.split(' ').filter(w => w.length > 2);
      const commonWords = queryWords.filter(word => titleWords.some(tw => tw.includes(word) || word.includes(tw)));
      titleScore = (commonWords.length / Math.max(queryWords.length, 1)) * 0.6;
    }
    
    let themeScore = 0;
    if (themeLower.includes(queryLower)) {
      themeScore = 0.3;
    }
    
    let activityScore = 0;
    if (objective.phase === 'action') activityScore = 0.1;
    else if (objective.phase === 'stratégie') activityScore = 0.05;
    
    return titleScore + themeScore + activityScore;
  };

  const getFilteredObjectives = () => {
    if (!objectiveSearchQuery.trim()) {
      return sortObjectivesByDate(availableObjectives).slice(0, 10);
    }
    
    const scoredObjectives = availableObjectives.map(obj => ({
      ...obj,
      similarity: calculateSimilarity(objectiveSearchQuery, obj)
    })).filter(obj => obj.similarity > 0.1)
      .sort((a, b) => b.similarity - a.similarity);
    
    return sortObjectivesByDate(scoredObjectives).slice(0, 10);
  };

  const handleObjectiveChange = (objective) => {
    console.log('Changement d\'objectif:', getObjectiveDisplayName(objective) || 'Aucun');

    onObjectiveSelect(objective);
    setShowObjectiveDropdown(false);

    if (onResetSynthesis) {
      onResetSynthesis();
    }

    if (objective) {
      console.log('Lancement découverte pour nouvel objectif:', getObjectiveDisplayName(objective));
      setTimeout(() => {
        onDiscoverForObjective(objective);
      }, 100);
    }
  };

  // ================================
  // SYNTHÈSE AVEC BOUTON D'ACTUALISATION
  // ================================
  
  const renderNewsSynthesis = () => {
    // Afficher la synthèse si elle existe ou si on charge
    const shouldShowSynthesis = newsSynthesis || loadingSynthesis;

    if (!shouldShowSynthesis) return null;

    return (
      <div className="mb-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-xl font-semibold text-blue-900 flex items-center gap-2">
                Synthèse Intelligente
                {selectedObjectiveForNews && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                    Objectif ciblé
                  </span>
                )}
              </h3>
              <p className="text-sm text-blue-700">
                {semanticAnalysisInfo && semanticAnalysisInfo.relevantFound > 0
                  ? `Basée sur ${semanticAnalysisInfo.relevantFound} source${semanticAnalysisInfo.relevantFound > 1 ? 's' : ''} analysée${semanticAnalysisInfo.relevantFound > 1 ? 's' : ''} par IA`
                  : filteredNews.length > 0
                    ? `Basée sur ${filteredNews.length} source${filteredNews.length > 1 ? 's' : ''}`
                    : 'Analyse des tendances actuelles'
                }
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {synthesisArticles.length > 0 && (
              <span className="flex items-center gap-1 text-sm text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                📰 {synthesisArticles.length} source{synthesisArticles.length > 1 ? 's' : ''}
              </span>
            )}
            
            {/* CORRECTION 4: Bouton d'actualisation fonctionnel */}
            <button
              onClick={handleRefreshSynthesis}
              disabled={loadingSynthesis || saving || filteredNews.length === 0}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={filteredNews.length === 0 ? "Aucun article à synthétiser" : "Régénérer la synthèse"}
            >
              {loadingSynthesis || saving ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span className="hidden sm:inline">{saving ? 'Sauvegarde...' : 'Génération...'}</span>
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  <span className="hidden sm:inline">Actualiser</span>
                </>
              )}
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {loadingSynthesis ? (
            <div className="flex items-center gap-3 text-blue-600">
              <RefreshCw className="h-5 w-5 animate-spin" />
              <span>Génération de la synthèse intelligente...</span>
            </div>
          ) : newsSynthesis && (
            <div className={`transition-all duration-300 ${expandedSynthesis ? '' : 'max-h-96 overflow-hidden'}`}>
              <div className="bg-white/70 border border-blue-200 rounded-lg p-4">
                <EnhancedMarkdown
                  content={newsSynthesis}
                  synthesisArticles={synthesisArticles}
                  onReadNews={onReadNews}
                  className="text-gray-700"
                />
              </div>
            </div>
          )}
          
          {newsSynthesis && newsSynthesis.length > 800 && (
            <button
              onClick={() => setExpandedSynthesis(!expandedSynthesis)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
            >
              {expandedSynthesis ? 'Réduire' : 'Voir plus'}
            </button>
          )}
        </div>

        {/* Sources utilisées */}
        {synthesisArticles && synthesisArticles.length > 0 && (
          <div className="mt-6 pt-4 border-t border-blue-200">
            <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              📰 Sources référencées
            </h4>
            <div className="space-y-2">
              {synthesisArticles.slice(0, 6).map((article, index) => (
                <button
                  key={`source-${article.id || index}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (article.url && article.url !== '#') {
                      window.open(article.url, '_blank', 'noopener,noreferrer');
                    } else if (onReadNews && typeof onReadNews === 'function') {
                      onReadNews(article);
                    }
                  }}
                  className="w-full text-left p-3 bg-white/70 hover:bg-blue-50 border border-blue-200 hover:border-blue-300 rounded-lg transition-all duration-200 group cursor-pointer"
                  type="button"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 group-hover:text-blue-700 text-sm leading-tight mb-1 break-words">
                        {article.title}
                      </p>
                      <p className="text-xs text-gray-600 group-hover:text-blue-600 flex items-center gap-2">
                        <span className="break-words">{article.source}</span>
                        {article.semanticScore && (
                          <>
                            <span>•</span>
                            <span className="bg-blue-100 text-blue-700 px-1 rounded text-xs">
                              Score : {article.semanticScore.toFixed(1)}
                            </span>
                          </>
                        )}
                      </p>
                    </div>
                    <span className="text-gray-400 group-hover:text-blue-500 text-lg flex-shrink-0">↗</span>
                  </div>
                </button>
              ))}
              
              {synthesisArticles.length > 6 && (
                <div className="text-center text-sm text-gray-500 py-2">
                  + {synthesisArticles.length - 6} autre{synthesisArticles.length - 6 > 1 ? 's' : ''} article{synthesisArticles.length - 6 > 1 ? 's' : ''} utilisé{synthesisArticles.length - 6 > 1 ? 's' : ''} dans cette synthèse
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ================================
  // RENDU PRINCIPAL
  // ================================

  return (
    <div className="mb-8">
      {/* Titre principal et boutons d'action */}
      <div className="flex flex-col gap-2 mb-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-1 flex items-center gap-2">
              <Zap className="h-7 w-7 text-blue-600 fill-current" />
              News
              {selectedObjectiveForNews && (
                <span className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                  Mode Objectif
                </span>
              )}
              <DiscoveryProgressIndicator />
            </h1>

            <p className="text-base text-gray-600 leading-relaxed">
              {selectedObjectiveForNews
                ? `Actualités ${showSemanticAnalysis && semanticAnalysisInfo ? 'analysées par IA' : 'sélectionnées'} pour votre objectif`
                : 'Actualités pertinentes pour vos objectifs'
              }
            </p>
          </div>

          {/* Boutons d'action */}
          <div className="flex items-center gap-3 ml-4 mt-2 sm:mt-0">
            <button
              onClick={onShowWatchlist}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm text-sm font-medium"
            >
              <Bell className="h-4 w-4" />
              Veille
            </button>
          </div>
        </div>
      </div>

      {/* Carte Actualités Ciblées */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Target className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-blue-900">
                Actualités Ciblées
              </h3>
              <p className="text-base text-blue-700">
                {selectedObjectiveForNews
                  ? showSemanticAnalysis && semanticAnalysisInfo && semanticAnalysisInfo.totalAnalyzed > 0
                    ? 'Analyse sémantique active'
                    : 'Objectif sélectionné'
                  : 'Recherchez des actualités pour vos objectifs'
                }
              </p>
            </div>
          </div>

          {selectedObjectiveForNews && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => onDiscoverForObjective(selectedObjectiveForNews)}
                disabled={isDiscovering}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm font-medium shadow-sm"
              >
                {isDiscovering ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    <span className="hidden sm:inline">Actualiser</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    <span className="hidden sm:inline">Actualiser</span>
                  </>
                )}
              </button>
              <button
                onClick={() => setShowObjectiveDropdown(!showObjectiveDropdown)}
                disabled={loadingObjectives}
                className="flex items-center gap-2 px-4 py-2 bg-blue-200 text-blue-800 rounded-lg hover:bg-blue-300 transition-colors shadow-sm disabled:opacity-50 text-sm font-medium"
              >
                <Target className="h-4 w-4" />
                Changer
              </button>
            </div>
          )}

          {!selectedObjectiveForNews && (
            <button
              onClick={() => setShowObjectiveDropdown(!showObjectiveDropdown)}
              disabled={loadingObjectives}
              className="flex items-center gap-2 px-4 py-2 bg-blue-200 text-blue-800 rounded-lg hover:bg-blue-300 transition-colors shadow-sm disabled:opacity-50 text-sm"
            >
              <Target className="h-4 w-4" />
              Mes Objectifs
            </button>
          )}
        </div>

        {/* Panneau de sélection d'objectifs */}
        <div>
            {/* Objectif sélectionné */}
            {selectedObjectiveForNews && (
              <div>
                {/* Carte de description de l'objectif */}
                <div className="bg-white/70 border border-blue-300 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      <div>
                        <div className="font-semibold text-blue-900 break-words">
                          {getObjectiveDisplayName(selectedObjectiveForNews)}
                        </div>
                        {(selectedObjectiveForNews.theme || selectedObjectiveForNews.chapitre) && (
                          <div className="text-sm text-blue-700">
                            📂 Thème : {selectedObjectiveForNews.theme || selectedObjectiveForNews.chapitre}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleObjectiveChange(null)}
                      className="text-blue-600 hover:text-blue-800 bg-blue-100 hover:bg-blue-200 p-2 rounded-lg transition-colors"
                      title="Désélectionner l'objectif"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Sélecteur d'objectifs */}
            {showObjectiveDropdown && (
              <div className="bg-slate-50/90 rounded-lg border border-slate-200 p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900">Choisir un objectif</h4>
                  <button
                    onClick={() => setShowObjectiveDropdown(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                
                <div className="mb-4">
                  <input
                    type="text"
                    value={objectiveSearchQuery}
                    onChange={(e) => setObjectiveSearchQuery(e.target.value)}
                    placeholder="Rechercher parmi vos objectifs..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 bg-white shadow-sm text-sm"
                  />
                </div>

                <div className="max-h-48 overflow-y-auto space-y-2">
                  {loadingObjectives ? (
                    <div className="text-center py-4">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto text-slate-500" />
                      <p className="text-sm text-gray-500 mt-2">Chargement des objectifs...</p>
                    </div>
                  ) : objectivesError ? (
                    <div className="text-center py-4 text-red-600">
                      <AlertTriangle className="h-6 w-6 mx-auto mb-2" />
                      <p className="text-sm">Erreur lors du chargement des objectifs</p>
                    </div>
                  ) : (
                    getFilteredObjectives().map((objective) => (
                      <button
                        key={objective.id}
                        onClick={() => handleObjectiveChange(objective)}
                        className={`w-full text-left p-3 rounded-lg transition-colors border ${
                          selectedObjectiveForNews?.id === objective.id
                            ? 'bg-blue-100 border-blue-300'
                            : 'hover:bg-slate-100 border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="font-medium text-gray-900 text-sm break-words">
                          🎯 {getObjectiveDisplayName(objective)}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center justify-between mt-1">
                          <span className="truncate">{objective.theme || objective.chapitre}</span>
                          {objective.createdAt && (
                            <span className="text-xs text-gray-400 ml-2">
                              {formatObjectiveDate(objective)}
                            </span>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                  
                  {!loadingObjectives && !objectivesError && getFilteredObjectives().length === 0 && (
                    <div className="text-center py-6 text-gray-500 text-sm">
                      {objectiveSearchQuery ? 'Aucun objectif trouvé' : 'Aucun objectif disponible'}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Informations sur le ciblage actuel */}
            {!selectedObjectiveForNews && !showObjectiveDropdown && (
              <div className="text-center py-8">
                <div className="text-blue-600 mb-4">
                  <Target className="h-12 w-12 mx-auto mb-3" />
                </div>
                <h4 className="text-lg font-semibold text-blue-900 mb-2">Ciblage intelligent</h4>
                <p className="text-base text-blue-700 mb-4">
                  Choisissez un de vos objectifs pour découvrir des actualités pertinentes grâce à l'analyse sémantique IA
                </p>
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => setShowObjectiveDropdown(true)}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Parcourir mes objectifs
                  </button>
                </div>
              </div>
            )}
          </div>
      </div>

      {/* Bloc d'analyse sémantique */}
      <SemanticAnalysisInfo />

      {/* Synthèse avec markdown corrigé */}
      {renderNewsSynthesis()}
    </div>
  );
};

export default NewsHeader;
